import { useSignal } from '@preact/signals';
import { useEffect } from 'preact/hooks';
import { useLocation } from 'preact-iso';

import { t } from '@amumin/i18n';
import { Button, Notice, TextField } from '@amumin/ui';

import { api, errorMessage } from '../api.js';
import { AuthLayout, tokenFromUrl } from '../components/AuthLayout.js';
import { isValidEmail, normalizeEmail } from '../lib/validation.js';
import { messages } from '../state/session.js';

/** Wachtwoord vergeten: de server antwoordt altijd 200 (geen account-enumeratie). */
export function ResetPage() {
  const { query, route } = useLocation();
  const m = messages.value;
  // Oude of doorgestuurde resetlink (`/reset#token=…`): het formulier voor een nieuw wachtwoord
  // staat op /reset/confirm. Het fragment gaat mee, de token komt nooit in de query of de logs.
  const token = tokenFromUrl(query);
  useEffect(() => {
    if (token) route(`/reset/confirm${window.location.hash}`, true);
    // Ook als alleen het fragment verandert (link geopend terwijl /reset al open stond).
    const onHash = () => {
      if (tokenFromUrl({})) route(`/reset/confirm${window.location.hash}`, true);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [token, route]);
  const email = useSignal('');
  const fieldError = useSignal<string | undefined>(undefined);
  const error = useSignal<string | undefined>(undefined);
  const busy = useSignal(false);
  const sent = useSignal<string | null>(null);

  const submit = async () => {
    if (!isValidEmail(email.value)) {
      fieldError.value = m.web.emailInvalid;
      return;
    }
    fieldError.value = undefined;
    busy.value = true;
    error.value = undefined;
    try {
      const normalized = normalizeEmail(email.value);
      await api.app.auth.resetRequest(normalized);
      sent.value = normalized;
    } catch (e) {
      error.value = errorMessage(e, m);
    } finally {
      busy.value = false;
    }
  };

  return (
    <AuthLayout title={m.web.resetTitle}>
      {sent.value ? (
        <Notice kind="success" name="reset-sent">
          {t(m.web.resetSent, { email: sent.value })}
        </Notice>
      ) : (
        <p>{m.web.resetIntro}</p>
      )}
      {error.value ? <Notice kind="error">{error.value}</Notice> : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <TextField
          id="email"
          label={m.web.email}
          type="email"
          value={email.value}
          onInput={(v) => (email.value = v)}
          autoComplete="username"
          inputMode="email"
          error={fieldError.value}
          required
        />
        <Button name="reset-request" type="submit" variant="primary" block busy={busy.value}>
          {m.web.resetSend}
        </Button>
      </form>
      <p class="auth-links">
        <a href="/login">{m.web.toLogin}</a>
      </p>
    </AuthLayout>
  );
}
